import type { BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import { createEventHook } from '@vueuse/core';
import { markRaw } from 'vue';
import { useNuxtApp } from '#app/nuxt';
import { defineStore } from '#imports';
import { resolveBuiltinBlock, resolveSerializedBlock } from '#layers/block-engine/utils/block/resolve';

export const useBlockResolverStore = defineStore('block-resolver', () => {
	const app = useNuxtApp();

	/**
	 * Cache for resolved blocks to avoid fetching the same block multiple times (even between server and client).
	 */
	const cache: Record<string, BlockSerialized | BlockDefinition> = app.payload.state['ds-blocks'] = app.payload.state['ds-blocks'] ?? {};

	async function resolveBlockSerializedCached(blockId: string) {
		if (cache[blockId]) {
			return cache[blockId];
		}

		const block = await resolveSerializedBlock(blockId);

		if (block) {
			cache[blockId] = block;
		}

		return block;
	}

	/**
	 * Resolves a block by id.
	 *
	 * The function remains `async` even though builtin blocks resolve
	 * synchronously via `resolveBuiltinBlock`: serialized (user-defined)
	 * blocks still require an async fetch, and callers currently await the
	 * return value uniformly. Do not convert this to a sync function without
	 * auditing every call site — the async signature is the public contract.
	 */
	async function resolveBlock(blockId: string): Promise<BlockSerialized | BlockDefinition> {
		const block = resolveBuiltinBlock(blockId) ?? (await resolveBlockSerializedCached(blockId));

		if (!block) {
			throw new Error(`Block not found: ${blockId}`);
		}

		return block;
	}

	function clearCache() {
		for (const key of Object.keys(cache)) delete cache[key];
	}

	const refreshHook = createEventHook<string>();

	/**
	 * Used to trigger a refresh of a block when it's edited.
	 *
	 * Example: use the block editor on a page using the studio toolbar.
	 */
	function onBlockRefresh(callback: (blockId: string) => void): { off: () => void } {
		if (import.meta.server) {
			return { off: () => {} };
		}

		return refreshHook.on(callback);
	}

	/**
	 * Re-render the block with the given ID
	 */
	function triggerBlockRefresh(blockId: string) {
		if (import.meta.server) {
			return;
		}

		delete cache[blockId];
		refreshHook.trigger(blockId);
	}

	return {
		resolveBlock,
		clearCache,
		cache: markRaw(cache),
		onBlockRefresh,
		triggerBlockRefresh,
	};
});
